/**
 * Capacity Heatmap Page - Sprint 14
 * PM-2: Portfolio Capacity Roll-Up
 * 
 * Heatmap visualization of capacity utilization
 * With bottleneck identification and drill-down
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, Users, TrendingUp, ChevronRight,
  Loader2, RefreshCw, Filter, Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  // TENANT ISOLATION: Include X-Tenant-ID header for sysadmin impersonation
  const impersonatedTenantId = localStorage.getItem('impersonated_tenant_id');
  const userTenantId = localStorage.getItem('simpliview_user_tenant_id');
  const activeTenantId = impersonatedTenantId || userTenantId;
  
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (activeTenantId) {
    headers['X-Tenant-ID'] = activeTenantId;
  }
  return headers;
};

// Heat Level Cell Component with Tooltip
const HeatCell = ({ data, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const heatColors = {
    critical: 'bg-red-500 hover:bg-red-600',
    high: 'bg-orange-400 hover:bg-orange-500',
    medium: 'bg-amber-300 hover:bg-amber-400',
    low: 'bg-green-400 hover:bg-green-500'
  };
  
  const textColors = {
    critical: 'text-white',
    high: 'text-white',
    medium: 'text-gray-900',
    low: 'text-white'
  };
  
  const gap = (data.demand_fte || 0) - (data.capacity_fte || 0);
  const gapStatus = gap > 0 ? 'over' : gap < 0 ? 'under' : 'balanced';
  
  return (
    <div
      onClick={() => onClick(data)}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      className={`
        ${heatColors[data.heat_level]} ${textColors[data.heat_level]}
        p-4 rounded-lg cursor-pointer transition-all transform hover:scale-105
        shadow-sm hover:shadow-md relative
      `}
      data-testid={`heat-cell-${data.team_id || data.team_name}`}
    >
      <div className="font-semibold text-sm truncate">{data.team_name}</div>
      <div className="text-2xl font-bold mt-1">{data.utilization_pct}%</div>
      <div className="text-xs opacity-80 mt-1">
        {data.demand_fte} / {data.capacity_fte} FTE
      </div>
      <div className="text-xs opacity-70 mt-1">
        {data.project_count} projects
      </div>
      
      {/* Hover Tooltip */}
      {showTooltip && (
        <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl">
          <div className="font-semibold mb-2">{data.team_name}</div>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="opacity-70">Required FTE:</span>
              <span className="font-medium">{data.demand_fte?.toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-70">Available FTE:</span>
              <span className="font-medium">{data.capacity_fte?.toFixed(1)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
              <span className="opacity-70">Gap:</span>
              <span className={`font-medium ${
                gapStatus === 'over' ? 'text-red-400' : 
                gapStatus === 'under' ? 'text-green-400' : 'text-gray-300'
              }`}>
                {gapStatus === 'over' ? '+' : ''}{gap?.toFixed(1)} FTE
              </span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-70">Utilization:</span>
              <span className={`font-medium ${
                data.utilization_pct > 100 ? 'text-red-400' : 
                data.utilization_pct > 90 ? 'text-amber-400' : 'text-green-400'
              }`}>
                {data.utilization_pct}%
              </span>
            </div>
          </div>
          {/* Tooltip arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
};

// Bottleneck Alert Component
const BottleneckAlert = ({ bottleneck }) => {
  const severityColors = {
    critical: 'bg-red-50 border-red-200 text-red-800',
    high: 'bg-orange-50 border-orange-200 text-orange-800',
    medium: 'bg-amber-50 border-amber-200 text-amber-800'
  };
  
  const severityIcons = {
    critical: '🚨',
    high: '⚠️',
    medium: '📊'
  };
  
  return (
    <div className={`p-3 rounded-lg border ${severityColors[bottleneck.severity]}`}>
      <div className="flex items-start gap-2">
        <span className="text-lg">{severityIcons[bottleneck.severity]}</span>
        <div>
          <div className="font-medium">
            {bottleneck.type === 'role_shortage' 
              ? `${bottleneck.role} Shortage`
              : `${bottleneck.team_name} Overloaded`
            }
          </div>
          <div className="text-sm opacity-80">
            {bottleneck.type === 'role_shortage'
              ? `Gap: ${bottleneck.gap_fte?.toFixed(1)} FTE`
              : `Utilization: ${bottleneck.utilization_pct}%`
            }
          </div>
        </div>
      </div>
    </div>
  );
};

// Role Capacity Bar Component
const RoleCapacityBar = ({ role, demand, allocated }) => {
  const utilization = allocated > 0 ? (demand / allocated) * 100 : 0;
  const gap = demand - allocated;
  
  let barColor = 'bg-green-500';
  if (utilization > 100) barColor = 'bg-red-500';
  else if (utilization > 90) barColor = 'bg-orange-500';
  else if (utilization > 70) barColor = 'bg-amber-500';
  
  return (
    <div className="mb-4">
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium text-gray-700">{role}</span>
        <span className={gap > 0 ? 'text-red-600' : 'text-green-600'}>
          {gap > 0 ? `+${gap.toFixed(1)}` : gap.toFixed(1)} FTE
        </span>
      </div>
      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${Math.min(utilization, 100)}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>Demand: {demand.toFixed(1)} FTE</span>
        <span>Allocated: {allocated.toFixed(1)} FTE</span>
      </div>
    </div>
  );
};

// Team Detail Drawer
const TeamDetailDrawer = ({ team, isOpen, onClose }) => {
  if (!isOpen || !team) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-xl">
        <div className="p-4 border-b bg-gray-50">
          <h2 className="font-semibold text-lg">{team.team_name}</h2>
          <p className="text-sm text-gray-500">Capacity Details</p>
        </div>
        
        <div className="p-4 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{team.capacity_fte}</div>
              <div className="text-xs text-gray-500">Total Capacity</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className={`text-2xl font-bold ${
                team.utilization_pct > 100 ? 'text-red-600' : 
                team.utilization_pct > 90 ? 'text-orange-600' :
                'text-green-600'
              }`}>
                {team.utilization_pct}%
              </div>
              <div className="text-xs text-gray-500">Utilization</div>
            </div>
          </div>
          
          {/* Visual Bar */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Demand vs Capacity</span>
              <span className="font-medium">{team.demand_fte} / {team.capacity_fte} FTE</span>
            </div>
            <div className="h-6 bg-gray-200 rounded-full overflow-hidden relative">
              <div 
                className={`h-full ${
                  team.heat_level === 'critical' ? 'bg-red-500' :
                  team.heat_level === 'high' ? 'bg-orange-400' :
                  team.heat_level === 'medium' ? 'bg-amber-400' :
                  'bg-green-400'
                }`}
                style={{ width: `${Math.min(team.utilization_pct, 100)}%` }}
              />
              {/* Capacity line at 100% */}
              <div className="absolute top-0 bottom-0 right-0 w-0.5 bg-gray-800" />
            </div>
          </div>
          
          {/* Stats */}
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Projects</span>
              <span className="font-medium">{team.project_count}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Heat Level</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                team.heat_level === 'critical' ? 'bg-red-100 text-red-700' :
                team.heat_level === 'high' ? 'bg-orange-100 text-orange-700' :
                team.heat_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                'bg-green-100 text-green-700'
              }`}>
                {team.heat_level}
              </span>
            </div>
          </div>
          
          {/* Recommendations */}
          {team.utilization_pct > 90 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <div className="font-medium text-amber-800">Capacity Warning</div>
                  <div className="text-sm text-amber-700 mt-1">
                    {team.utilization_pct > 100 
                      ? 'This team is over capacity. Consider reassigning projects or hiring.'
                      : 'This team is approaching capacity. Monitor closely.'
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t">
          <button
            onClick={onClose}
            className="w-full py-2 border rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const CapacityHeatmap = () => {
  const [searchParams] = useSearchParams();
  const portfolioId = searchParams.get('portfolio_id');
  
  const [loading, setLoading] = useState(true);
  const [heatmapData, setHeatmapData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rollupData, setRollupData] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    team: '',
    role: '',
    period: '',
    heatLevel: ''
  });
  
  // Get unique filter options
  const getFilterOptions = () => {
    const teams = [...new Set(heatmapData.map(d => d.team_name))].sort();
    const roles = rollupData?.by_role ? Object.keys(rollupData.by_role).sort() : [];
    return { teams, roles };
  };
  
  // Apply filters
  useEffect(() => {
    let data = [...heatmapData];
    
    if (filters.team) {
      data = data.filter(d => d.team_name === filters.team);
    }
    
    if (filters.heatLevel) {
      data = data.filter(d => d.heat_level === filters.heatLevel);
    }
    
    // Role filter - filter based on role assignments
    if (filters.role && rollupData?.by_role) {
      const roleTeams = Object.entries(rollupData.by_role)
        .filter(([role]) => role === filters.role)
        .flatMap(([, data]) => data.teams || []);
      data = data.filter(d => roleTeams.includes(d.team_name));
    }
    
    setFilteredData(data);
  }, [heatmapData, filters, rollupData]);
  
  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      const params = portfolioId ? `?portfolio_id=${portfolioId}` : '';
      
      const [heatmapRes, rollupRes] = await Promise.all([
        axios.get(`${API_BASE}/portfolio-mgmt/capacity/heatmap${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/portfolio-mgmt/capacity/rollup${params}`, { headers: getAuthHeaders() })
      ]);
      
      setHeatmapData(heatmapRes.data.heatmap || []);
      setFilteredData(heatmapRes.data.heatmap || []);
      setSummary(heatmapRes.data.summary);
      setRollupData(rollupRes.data);
    } catch (err) {
      console.error('Failed to load capacity data:', err);
      toast.error('Failed to load capacity data');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
  }, [portfolioId]);
  
  // Clear filters
  const clearFilters = () => {
    setFilters({ team: '', role: '', period: '', heatLevel: '' });
  };
  
  const { teams, roles } = getFilterOptions();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 p-6" data-testid="capacity-heatmap">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Capacity Heatmap</h1>
          <p className="text-gray-500">Team utilization and bottleneck analysis</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${showFilters ? 'bg-blue-50 border-blue-300' : 'hover:bg-white'}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4" />
            Filters
            {(filters.team || filters.role || filters.heatLevel) && (
              <span className="w-2 h-2 rounded-full bg-blue-500" />
            )}
          </button>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-white"
            data-testid="refresh-heatmap-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Team</label>
              <select
                value={filters.team}
                onChange={(e) => setFilters({ ...filters, team: e.target.value })}
                className="border rounded px-3 py-2 text-sm min-w-[160px]"
                data-testid="filter-team"
              >
                <option value="">All Teams</option>
                {teams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Role</label>
              <select
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                className="border rounded px-3 py-2 text-sm min-w-[160px]"
                data-testid="filter-role"
              >
                <option value="">All Roles</option>
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Period</label>
              <select
                value={filters.period}
                onChange={(e) => setFilters({ ...filters, period: e.target.value })}
                className="border rounded px-3 py-2 text-sm min-w-[160px]"
                data-testid="filter-period"
              >
                <option value="">Current Period</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="this_quarter">This Quarter</option>
                <option value="next_month">Next Month</option>
              </select>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Heat Level</label>
              <select
                value={filters.heatLevel}
                onChange={(e) => setFilters({ ...filters, heatLevel: e.target.value })}
                className="border rounded px-3 py-2 text-sm min-w-[160px]"
                data-testid="filter-heat-level"
              >
                <option value="">All Levels</option>
                <option value="critical">Critical (&gt;100%)</option>
                <option value="high">High (90-100%)</option>
                <option value="medium">Medium (70-90%)</option>
                <option value="low">Low (&lt;70%)</option>
              </select>
            </div>
            
            {(filters.team || filters.role || filters.heatLevel || filters.period) && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 py-2"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-gray-500">Total Teams</div>
            <div className="text-2xl font-bold text-gray-900">{summary.total_teams}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-sm text-red-600">Critical</div>
            <div className="text-2xl font-bold text-red-700">{summary.critical}</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-sm text-orange-600">High</div>
            <div className="text-2xl font-bold text-orange-700">{summary.high}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-sm text-green-600">Healthy</div>
            <div className="text-2xl font-bold text-green-700">{summary.low + summary.medium}</div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Heatmap Grid */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border p-4">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Utilization
              </span>
              {filteredData.length !== heatmapData.length && (
                <span className="text-sm font-normal text-gray-500">
                  Showing {filteredData.length} of {heatmapData.length} teams
                </span>
              )}
            </h2>
            
            {filteredData.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredData.map((team, idx) => (
                  <HeatCell 
                    key={idx} 
                    data={team} 
                    onClick={setSelectedTeam}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{heatmapData.length > 0 ? 'No teams match the selected filters' : 'No team capacity data available'}</p>
                {heatmapData.length > 0 && (
                  <button 
                    onClick={clearFilters}
                    className="mt-2 text-blue-600 hover:underline text-sm"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
            
            {/* Legend */}
            <div className="mt-6 pt-4 border-t flex items-center justify-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-400" />
                <span className="text-xs text-gray-600">&lt;70% (Healthy)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-300" />
                <span className="text-xs text-gray-600">70-90% (Medium)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-orange-400" />
                <span className="text-xs text-gray-600">90-100% (High)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500" />
                <span className="text-xs text-gray-600">&gt;100% (Critical)</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Sidebar - Bottlenecks & Role Analysis */}
        <div className="space-y-6">
          {/* Bottlenecks */}
          {rollupData?.bottlenecks && rollupData.bottlenecks.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Bottlenecks ({rollupData.bottleneck_count})
              </h3>
              
              <div className="space-y-2">
                {rollupData.bottlenecks.map((b, idx) => (
                  <BottleneckAlert key={idx} bottleneck={b} />
                ))}
              </div>
            </div>
          )}
          
          {/* Capacity by Role */}
          {rollupData?.capacity?.by_role && rollupData.capacity.by_role.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Capacity by Role
              </h3>
              
              {rollupData.capacity.by_role.map((role, idx) => (
                <RoleCapacityBar
                  key={idx}
                  role={role.role}
                  demand={role.total_demand_fte}
                  allocated={role.total_allocated_fte}
                />
              ))}
            </div>
          )}
          
          {/* Info Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <div className="font-medium">Understanding the Heatmap</div>
                <p className="mt-1 text-blue-700">
                  Click on any team cell to view detailed capacity information. 
                  Teams in red require immediate attention.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Team Detail Drawer */}
      <TeamDetailDrawer
        team={selectedTeam}
        isOpen={!!selectedTeam}
        onClose={() => setSelectedTeam(null)}
      />
    </div>
  );
};

export default CapacityHeatmap;
